/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useRef } from "react"
import { useNavigate } from "react-router"

import { ApplicationStatus } from "../../features/application/application-types"
import type { ApplicationType, ThreatOverview } from "../../features/application-type/application-type-types"
import { AppRoutes } from "../../routes/routes"
import { prepareUrl } from "../../utils/prepare-application-url"
import { randomBetween } from "../../utils/random-between"
import { useAnimateValueEffect } from "../../utils/use-animate-value-effect"
import { valueOrDash } from "../../utils/value-or-dash"
import { AppIcon } from "../components/app-icon"
import { AppTitleDescription } from "../components/app-title-description"
import { CategoryPlatform } from "../components/category-platform"

import "./dashboard-row-view.css"

interface Props {
    appType: ApplicationType
    threat: ThreatOverview | null
}

export const DashboardRowView = observer(({ appType, threat }: Props) => {
    const navigate = useNavigate()

    const insecureRef = useRef<HTMLSpanElement>(null)
    const outdatedRef = useRef<HTMLSpanElement>(null)
    const activeRef = useRef<HTMLSpanElement>(null)

    useAnimateValueEffect(insecureRef, randomDuration(), threat?.insecure)
    useAnimateValueEffect(outdatedRef, randomDuration(), (threat?.outdatedIos ?? 0) + (threat?.outdatedAndroid ?? 0))
    useAnimateValueEffect(activeRef, randomDuration(), threat?.active)

    return (
        <tr className={"dashboard-row-view placeholder-glow col-12"}>
            <td className={"col-9"}>
                <div className={"info"}>
                    <AppIcon appTypeId={appType.id} className="icon" />
                    <div className="text-section">
                        <AppTitleDescription
                            description={valueOrDash(appType.description)}
                            sourcePackageName={valueOrDash(appType.sourcePackageName)}
                        />
                    </div>
                    <span className="category-platform">
                        <CategoryPlatform category={appType.category} platform={appType.platform} />
                    </span>
                </div>
            </td>
            {!threat && (
                <>
                    <td className="p-legacy">
                        <div className="placeholder bg-legacy-secondary w-full" />
                    </td>
                    <td className="p-legacy">
                        <div className="placeholder bg-legacy-secondary w-full" />
                    </td>
                    <td className="p-legacy">
                        <div className="placeholder bg-legacy-secondary w-full" />
                    </td>
                </>
            )}
            {threat && (
                <>
                    <td
                        className="col-1 clickable-td p-0"
                        onClick={() =>
                            navigate(prepareUrl(AppRoutes.Application, ApplicationStatus.insecure, appType.id))
                        }
                    >
                        <div className="stat-cell stat-cell-lock">
                            <span ref={insecureRef} className={`stat-number orange-text`} />
                        </div>
                    </td>

                    <td className="col-1">
                        <div className="stat-cell stat-cell-triangle">
                            <span ref={outdatedRef} className={`stat-number yellow-text`} />
                        </div>
                    </td>

                    <td
                        className="col-1 clickable-td"
                        onClick={() =>
                            navigate(prepareUrl(AppRoutes.Application, ApplicationStatus.active, appType.id))
                        }
                    >
                        <div className="stat-cell stat-cell-user">
                            <span ref={activeRef} className={`stat-number green-text`} />
                        </div>
                    </td>
                </>
            )}
        </tr>
    )
})

function randomDuration(): number {
    return randomBetween(1_000, 1_600)
}
